import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ApplicationStudioArtifactSummary } from '@tabbio/types';

// ─── Types ─────────────────────────────────────────────────────────────

export type CoverLetterTone =
  | 'professional'
  | 'enthusiastic'
  | 'concise'
  | 'storytelling';

export interface CoverLetter {
  id: string;
  title: string;
  company: string;
  role: string;
  tone: CoverLetterTone;
  body: string;
  updatedAt: string;
}

export type InterviewKind =
  | 'behavioral'
  | 'technical'
  | 'system_design'
  | 'screening';

export type InterviewStatus = 'draft' | 'in_progress' | 'completed';

export interface InterviewQuestion {
  id: string;
  prompt: string;
  category: string;
  hint?: string;
  answer?: string;
  feedback?: string;
}

export interface InterviewSession {
  id: string;
  title: string;
  role: string;
  company?: string;
  kind: InterviewKind;
  status: InterviewStatus;
  questions: InterviewQuestion[];
  updatedAt: string;
  durationMin: number;
}

// ─── Sample data ───────────────────────────────────────────────────────

const SAMPLE_COVER_LETTERS: CoverLetter[] = [
  {
    id: 'cl_1',
    title: 'Product Designer at Canonic',
    company: 'Canonic',
    role: 'Product Designer',
    tone: 'professional',
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    body:
      "Dear Hiring Manager,\n\nI was excited to come across the Product Designer opening at Canonic. With six years of designing AI-first interfaces, I am confident I can help your team turn complex backend tools into clear, calm experiences.\n\nAt Tabbio I led the redesign of the candidate workspace, lifting weekly active use by 38%. I work end-to-end, from research to typography, and partner closely with engineering to ship pixel-tight builds.\n\nI would love to bring this craft to Canonic and contribute to your visual system as it scales.\n\nWarm regards,\nAhmed Al Shamsi",
  },
  {
    id: 'cl_2',
    title: 'Frontend Engineer at Lattice',
    company: 'Lattice',
    role: 'Senior Frontend Engineer',
    tone: 'enthusiastic',
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 36).toISOString(),
    body:
      "Hi Lattice team,\n\nYour engineering blog on building reactive review cycles is exactly the kind of frontend work I want to be part of. I have shipped React Native and Next.js apps used by tens of thousands of people each week, and I am ready to do the same at your scale.\n\nMost recently I rebuilt our messaging system on top of Convex, halving p95 latency while keeping the experience offline-first.\n\nI would love to chat about how I can help raise the quality bar on the people-platform team.\n\nBest,\nAhmed",
  },
  {
    id: 'cl_3',
    title: 'Growth Lead at Notion',
    company: 'Notion',
    role: 'Growth Lead, MENA',
    tone: 'concise',
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
    body:
      "Hi team,\n\nI run growth for a Dubai-based AI career platform that grew from 0 to 80k MAU in 11 months on a small budget. I would love to bring that craft to Notion's MENA expansion.\n\nIn the past year I shipped a referral loop that drove 41% of paid signups, and a localized landing-page system that brought CAC down by 28%.\n\nHappy to share the playbook.\n\nAhmed",
  },
];

const SAMPLE_INTERVIEW_SESSIONS: InterviewSession[] = [
  {
    id: 'iv_1',
    title: 'Senior PM: final round',
    role: 'Senior Product Manager',
    company: 'Stripe',
    kind: 'behavioral',
    status: 'in_progress',
    durationMin: 35,
    updatedAt: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
    questions: [
      {
        id: 'q1',
        prompt:
          'Tell me about a time you shipped a launch under an unrealistic deadline. What did you cut and why?',
        category: 'Leadership',
        hint: 'Use the STAR structure. Land on the hardest tradeoff.',
        answer:
          'When we launched the Tabbio AI assistant, we had three weeks. I cut the team chat surface and kept document Q&A, the core promise, so we could keep voice quality high.',
      },
      {
        id: 'q2',
        prompt:
          'Walk me through a decision where the data and your gut disagreed.',
        category: 'Judgment',
        hint: 'Pick a story where the gut won and you can defend it.',
      },
      {
        id: 'q3',
        prompt:
          'How do you set quarterly goals for a team that owns a horizontal platform?',
        category: 'Strategy',
      },
    ],
  },
  {
    id: 'iv_2',
    title: 'Frontend systems: coding round',
    role: 'Frontend Engineer',
    company: 'Linear',
    kind: 'technical',
    status: 'draft',
    durationMin: 60,
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 26).toISOString(),
    questions: [
      {
        id: 'q1',
        prompt: 'Design a virtualized list that handles variable-height rows.',
        category: 'Coding',
        hint: 'Walk through measurement, caching, and recycling.',
      },
      {
        id: 'q2',
        prompt:
          'How would you structure local-first state for a multi-user editor?',
        category: 'Systems',
      },
    ],
  },
  {
    id: 'iv_3',
    title: 'Phone screen prep for Figma',
    role: 'Design Engineer',
    company: 'Figma',
    kind: 'screening',
    status: 'completed',
    durationMin: 25,
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4).toISOString(),
    questions: [
      {
        id: 'q1',
        prompt: 'Why Figma?',
        category: 'Motivation',
        answer:
          'I have been a paying customer for five years and the new design-engineering bar is the most exciting place in the industry to ship craft.',
        feedback:
          'Strong opening. Add one specific Figma artifact you admire to make it land harder.',
      },
      {
        id: 'q2',
        prompt: 'Walk me through a recent project end-to-end.',
        category: 'Project',
        answer:
          'I rebuilt the Tabbio onboarding from a step-form into a conversational flow, lifting completion from 41% to 67%.',
        feedback: 'Great. Tighten the middle and go straight to the metric.',
      },
    ],
  },
];

// ─── Context ────────────────────────────────────────────────────────────

interface ServicesMockState {
  coverLetters: CoverLetter[];
  interviewSessions: InterviewSession[];
  upsertCoverLetter: (next: CoverLetter) => void;
  removeCoverLetter: (id: string) => void;
  upsertInterviewSession: (next: InterviewSession) => void;
  removeInterviewSession: (id: string) => void;
}

const ServicesMockContext = createContext<ServicesMockState | null>(null);

export function ServicesMockProvider({ children }: { children: React.ReactNode }) {
  const [coverLetters, setCoverLetters] = useState<CoverLetter[]>(SAMPLE_COVER_LETTERS);
  const [interviewSessions, setInterviewSessions] = useState<InterviewSession[]>(
    SAMPLE_INTERVIEW_SESSIONS,
  );

  const upsertCoverLetter = useCallback((next: CoverLetter) => {
    setCoverLetters((prev) => {
      const idx = prev.findIndex((c) => c.id === next.id);
      if (idx === -1) return [next, ...prev];
      const copy = prev.slice();
      copy[idx] = next;
      return copy;
    });
  }, []);

  const removeCoverLetter = useCallback((id: string) => {
    setCoverLetters((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const upsertInterviewSession = useCallback((next: InterviewSession) => {
    setInterviewSessions((prev) => {
      const idx = prev.findIndex((s) => s.id === next.id);
      if (idx === -1) return [next, ...prev];
      const copy = prev.slice();
      copy[idx] = next;
      return copy;
    });
  }, []);

  const removeInterviewSession = useCallback((id: string) => {
    setInterviewSessions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const value = useMemo<ServicesMockState>(
    () => ({
      coverLetters,
      interviewSessions,
      upsertCoverLetter,
      removeCoverLetter,
      upsertInterviewSession,
      removeInterviewSession,
    }),
    [
      coverLetters,
      interviewSessions,
      removeCoverLetter,
      removeInterviewSession,
      upsertCoverLetter,
      upsertInterviewSession,
    ],
  );

  return (
    <ServicesMockContext.Provider value={value}>{children}</ServicesMockContext.Provider>
  );
}

export function useServicesMock() {
  const ctx = useContext(ServicesMockContext);
  if (!ctx) {
    throw new Error('useServicesMock must be used inside <ServicesMockProvider>');
  }
  return ctx;
}

// ─── Helpers ────────────────────────────────────────────────────────────

export function newCoverLetterId() {
  return `cl_${Date.now().toString(36)}`;
}

export function newInterviewSessionId() {
  return `iv_${Date.now().toString(36)}`;
}

export function isApplicationStudioArtifactId(id?: string | null) {
  if (!id) return false;
  return !id.startsWith('cl_') && !id.startsWith('iv_');
}

function artifactContent(artifact: ApplicationStudioArtifactSummary) {
  return artifact.content && typeof artifact.content === 'object'
    ? artifact.content
    : {};
}

function stringFromRecord(
  record: Record<string, unknown>,
  key: string,
  fallback = '',
) {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function parseArtifactTitle(
  artifact: ApplicationStudioArtifactSummary,
  prefix: string,
) {
  const content = artifactContent(artifact);
  const rawTitle =
    stringFromRecord(content, 'title') ||
    (artifact.title ?? '').replace(new RegExp(`^${prefix}:\\s*`, 'i'), '') ||
    prefix;
  const parts = rawTitle.split(/\s*(?:\u2014|-)\s*/u);
  const role = stringFromRecord(content, 'roleTitle', parts[0]?.trim() || rawTitle);
  const company = stringFromRecord(content, 'companyName', parts[1]?.trim() || '');

  return {
    title: company ? `${role}: ${company}` : role,
    role,
    company,
  };
}

function normalizeCoverLetterTone(value: unknown): CoverLetterTone {
  return value === 'enthusiastic' ||
    value === 'concise' ||
    value === 'storytelling' ||
    value === 'professional'
    ? value
    : 'professional';
}

function normalizeInterviewKind(value: unknown): InterviewKind {
  if (
    value === 'technical' ||
    value === 'system_design' ||
    value === 'screening' ||
    value === 'behavioral'
  ) {
    return value;
  }
  const text = typeof value === 'string' ? value.toLowerCase() : '';
  if (text.includes('technical')) return 'technical';
  if (text.includes('system')) return 'system_design';
  if (text.includes('screen')) return 'screening';
  return 'behavioral';
}

export function coverLetterFromApplicationStudioArtifact(
  artifact: ApplicationStudioArtifactSummary,
): CoverLetter | null {
  if (artifact.kind !== 'cover_letter') return null;
  const content = artifactContent(artifact);
  const body = stringFromRecord(content, 'body', artifact.plaintext ?? '');
  if (!body) return null;
  const target = parseArtifactTitle(artifact, 'Cover Letter');
  return {
    id: artifact.id,
    title: target.title,
    company: target.company || 'Target company',
    role: target.role,
    tone: normalizeCoverLetterTone(content.tone),
    body,
    updatedAt: artifact.updatedAt,
  };
}

export function interviewSessionFromApplicationStudioArtifact(
  artifact: ApplicationStudioArtifactSummary,
): InterviewSession | null {
  if (artifact.kind !== 'interview_prep') return null;
  const content = artifactContent(artifact);
  const target = parseArtifactTitle(artifact, 'Interview Prep');
  const questions = Array.isArray(content.questions)
    ? content.questions
        .flatMap((question, index): InterviewQuestion[] => {
          if (!question || typeof question !== 'object') return [];
          const value = question as Record<string, unknown>;
          const prompt = stringFromRecord(value, 'prompt');
          if (!prompt) return [];
          const next: InterviewQuestion = {
            id: stringFromRecord(value, 'id', `q${index + 1}`),
            prompt,
            category: stringFromRecord(value, 'category', 'Interview'),
          };
          const hint = stringFromRecord(value, 'hint');
          const answer = stringFromRecord(value, 'answer');
          const feedback = stringFromRecord(value, 'feedback');
          if (hint) next.hint = hint;
          if (answer) next.answer = answer;
          if (feedback) next.feedback = feedback;
          return [next];
        })
    : [];
  if (questions.length === 0) return null;

  const duration = Number(content.durationMinutes);
  const allAnswered = questions.every((question) => question.answer);
  return {
    id: artifact.id,
    title: target.title,
    role: target.role,
    company: target.company || undefined,
    kind: normalizeInterviewKind(content.practiceKind),
    status: allAnswered ? 'completed' : 'in_progress',
    durationMin: Number.isFinite(duration) && duration > 0 ? duration : 30,
    questions,
    updatedAt: artifact.updatedAt,
  };
}

export const COVER_LETTER_TONE_OPTIONS: { value: CoverLetterTone; key: string }[] = [
  { value: 'professional', key: 'coverLetters.tone.professional' },
  { value: 'enthusiastic', key: 'coverLetters.tone.enthusiastic' },
  { value: 'concise', key: 'coverLetters.tone.concise' },
  { value: 'storytelling', key: 'coverLetters.tone.storytelling' },
];

export const INTERVIEW_KIND_OPTIONS: { value: InterviewKind; key: string }[] = [
  { value: 'behavioral', key: 'interviewPrep.kind.behavioral' },
  { value: 'technical', key: 'interviewPrep.kind.technical' },
  { value: 'system_design', key: 'interviewPrep.kind.systemDesign' },
  { value: 'screening', key: 'interviewPrep.kind.screening' },
];

export const INTERVIEW_STATUS_COLORS: Record<InterviewStatus, string> = {
  draft: '#94a3b8',
  in_progress: '#5e29ff',
  completed: '#177245',
};

export function generateMockCoverLetter(input: {
  company: string;
  role: string;
  tone: CoverLetterTone;
  notes?: string;
}) {
  const opener =
    input.tone === 'enthusiastic'
      ? `I lit up the moment I saw the ${input.role} opening at ${input.company}.`
      : input.tone === 'concise'
        ? `I am applying for the ${input.role} role at ${input.company}.`
        : input.tone === 'storytelling'
          ? `Six years ago I built my first product the night before a demo. That is the energy I want to bring to ${input.company}.`
          : `Dear ${input.company} hiring team,\n\nI am writing to express my interest in the ${input.role} role.`;

  const middle = input.notes?.trim()
    ? `\n\n${input.notes.trim()}`
    : `\n\nMy recent work has been at the intersection of design and engineering, shipping AI-native experiences that feel calm rather than crowded. I care about craft, momentum, and being a good teammate.`;

  const closer =
    input.tone === 'concise'
      ? `\n\nHappy to share more in a short call.\n\nAhmed`
      : `\n\nI would love the chance to meet your team and dig into where I can contribute fastest.\n\nWarm regards,\nAhmed Al Shamsi`;

  return `${opener}${middle}${closer}`;
}

export function generateMockInterviewQuestions(
  kind: InterviewKind,
): InterviewQuestion[] {
  const base: Record<InterviewKind, InterviewQuestion[]> = {
    behavioral: [
      {
        id: 'b1',
        prompt: 'Tell me about a time you disagreed with a manager. How did you handle it?',
        category: 'Communication',
        hint: 'STAR format. Land on what you learned.',
      },
      {
        id: 'b2',
        prompt: 'Describe your proudest shipped project and what made it work.',
        category: 'Impact',
      },
      {
        id: 'b3',
        prompt: 'When did you fail, and what did you change after?',
        category: 'Self-awareness',
      },
    ],
    technical: [
      {
        id: 't1',
        prompt: 'Walk me through a recent technical decision and the alternatives you ruled out.',
        category: 'Architecture',
      },
      {
        id: 't2',
        prompt: 'Design a rate-limiter for a public API.',
        category: 'Systems',
        hint: 'Cover hot keys and burst behavior.',
      },
      {
        id: 't3',
        prompt: 'How do you approach reducing flaky tests in a large codebase?',
        category: 'Quality',
      },
    ],
    system_design: [
      {
        id: 's1',
        prompt: 'Design a real-time collaborative document editor.',
        category: 'High-level',
        hint: 'Talk CRDTs vs OT and your storage model.',
      },
      {
        id: 's2',
        prompt: 'Design a job-board search system handling 10M postings.',
        category: 'Scale',
      },
    ],
    screening: [
      {
        id: 'p1',
        prompt: 'Walk me through your background in 2 minutes.',
        category: 'Intro',
      },
      {
        id: 'p2',
        prompt: 'Why this company, and why now?',
        category: 'Motivation',
        hint: 'Be specific. Mention something only an insider would know.',
      },
      {
        id: 'p3',
        prompt: 'What are you looking for in your next role?',
        category: 'Fit',
      },
    ],
  };

  return base[kind].map((q) => ({ ...q }));
}

export function formatRelativeShort(iso: string, locale: string) {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = Math.round(diff / 60000);
  if (min < 1) return locale.startsWith('ar') ? 'الآن' : 'Now';
  if (min < 60) return locale.startsWith('ar') ? `قبل ${min} د` : `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return locale.startsWith('ar') ? `قبل ${hr} س` : `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return locale.startsWith('ar') ? `قبل ${day} ي` : `${day}d ago`;
  const d = new Date(iso);
  return d.toLocaleDateString(locale.startsWith('ar') ? 'ar' : 'en', {
    month: 'short',
    day: 'numeric',
  });
}
